import React, { useMemo, useEffect, useState } from 'react';
import { Button, DropdownProps, Loader, Tab, TabPane } from 'semantic-ui-react';
import HFCAssetForm from './HFCAssetForm';
import HFCMetadata from './HFCMetadata';
import HFCPhysical from './HFCPhysical';
import agent from '../../../app/api/agent';
import { v4 as uuid } from 'uuid';
import { initialHFCActivityState } from '../../../app/models/activity';
import { useStore } from '../../../app/stores/store';
import { toast } from 'react-toastify';
import { historyInitialState } from '../../../app/models/history';
import { debounce } from 'lodash';
import { observer } from 'mobx-react-lite';

interface AssetType {
  id: string;
  typeName: string;
}

interface ElementType {
  id: string;
  elementName: string;
}

interface ContainerBarcode {
  container_barcode: string;
  reel_count: string;
  reel_barcode: string[];
}


interface FormValues {
  title: string;
  assetType: string;
  elementType: string;
  assetId: string;
  print: string;
  containerBarcodes:ContainerBarcode[];
  notes: string;
  [key: string]: string | ContainerBarcode[]; 
}

function HfcRegister() {
  const{userStore, dashboardStore}= useStore()
  const [assetData, setAssetData] = useState<AssetType[]>([]);
  const [elementData, setElementData] = useState<ElementType[]>([]);
  const [metaData, setMetaData] = useState<any[]>([]);
  const [titleSearch, setTitleSearch] = useState<any[]>([]);
  const [selectedAsset, setSelectedAsset] = useState<string | undefined>();
  const [selectedElement, setSelectedElement] = useState<string | undefined>();
  const [isChecked, setIsChecked] = useState<boolean>(false);
  const [registerLoading, setRegisterLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);


  const [activeIndex, setActiveIndex] = useState<number>(0);

  const initialContainerBarcode: ContainerBarcode = {
    container_barcode: '',
    reel_count: '',
    reel_barcode: [],
  };
  const initialFormValues: FormValues = {
    title: '',
    assetType: '',
    elementType: '',
    assetId: '',  
    print: '',
    containerBarcodes: [initialContainerBarcode],
    notes: '',
  };

  const [formValues, setFormValues] = useState<FormValues>(initialFormValues);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await agent.HFC.registerAsset();
        setAssetData(res.data);
      } catch (error) {
        console.error("Error registering asset:", error);
      }
    };

    fetchData();
  }, []);

  const handleAssetTypeChange = async (event: React.SyntheticEvent<HTMLElement, Event>, data: DropdownProps) => {
    const selectedValue = data.value as string;
    const selectedIndex = assetData.findIndex((option: any) => option.id === selectedValue);
    if (selectedIndex !== -1) { 
        const selectedOption:any = assetData[selectedIndex];
        setSelectedAsset(selectedOption?.typeName);
        try {
            const elemRes = await agent.HFC.registerElementType(selectedValue);
            setElementData(elemRes.data);
            setFormValues({...formValues, assetType: selectedValue});
        } catch (error) {
            console.error("Error making API call:", error);
        }
    } else {
        console.log("Selected asset not found in assetData");
    }
};

  
  const handleCheckboxChange = async () => {
    setIsChecked(!isChecked)
    if (!isChecked) {
      try {
        const res = await agent.HFC.assetIdGenerate(formValues.title, selectedAsset, selectedElement);
        setFormValues({ ...formValues, assetId: res.data });
      } catch (error) {
        console.error('Error occurred:', error);
      }
    }else{
      setFormValues({ ...formValues, assetId: '' });
    }
  };

  const handleElementTypeChange = async (event: React.SyntheticEvent<HTMLElement, Event>, data: DropdownProps) => {
    const selectedValue = data.value as string;
    const selectedIndex = elementData.findIndex((option: any) => option.id === selectedValue);

    if (selectedIndex !== -1) {
        const selectedOption:any = elementData[selectedIndex];
        setSelectedElement(selectedOption?.elementName);
    }

    try {
        const res = await agent.HFC.registerMetadata(selectedValue);
        setFormValues({...formValues, elementType: selectedValue});
        const filteredData = res.data.filter((item: any) => item.enableByDefault);
        setMetaData(filteredData);
    } catch (error) {
        console.error("Error making API call:", error);
    }
};

  const goToNextTab = () => {
    setActiveIndex((prevIndex) => prevIndex + 1); 
  };

  const isSubmitDisabled = () => {
    if (formValues.containerBarcodes.some(obj =>
        Object.values(obj).some(value => value === '' || (Array.isArray(value) && value.length === 0)))) {
        return true;
    }

    for (const item of metaData) {
        if (item.isMandatory && item.elementValue === '') {
            return true;
        }
    }

    for (const key in formValues) {
        if (Array.isArray(formValues[key])) {
            const arrayValue = formValues[key] as ContainerBarcode[];
            if (arrayValue.some(obj => obj.reel_barcode.some(value => value.trim() === ''))) {
                return true;
            }
        } else {
            // For non-array values, perform the existing checks
            if (key !== 'notes' && key !== 'print' && formValues[key] === '') {
                return true;
            }
        }
    }

    return false;
};

  const resetFormValues = () => {
    setFormValues(initialFormValues);
  };
  const resetFormKeyValues = () => {
    const updatedMetaData = metaData.map((item) => ({
      ...item,
      elementValue: '' 
    }));
    setMetaData(updatedMetaData);
  };
  
  const handleSubmit = async () => {
    setRegisterLoading(true);

    const res = await agent.HFC.registerWorkLevel(formValues.title);

    const reelArray = [];
    for (const item of formValues.containerBarcodes) {
        for (const reel of item.reel_barcode) {
            reelArray.push(reel);
        }
    }

    const resval = await agent.HFC.HFCreelBarcodeVal(reelArray);
    if (resval.data !== 'Okay') {
        toast.error(resval.data);
        setRegisterLoading(false);
        return;
    }

    const itemGroupId = res.data;

    for (const item of formValues.containerBarcodes) {
        for (const reel of item.reel_barcode) {
            const clonedMetaData = metaData.map((metaItem) => {
                const clonedItem = { ...metaItem };
                if (Array.isArray(clonedItem.options)) {
                    clonedItem.options = clonedItem.options.map((option:any) => ({ ...option }));
                }
                return clonedItem;
            });

            clonedMetaData.forEach((metaItem) => {
                metaItem.elementId = uuid();
                if (Array.isArray(metaItem.options)) {
                    metaItem.options.forEach((option:any) => {
                        option.optionId = uuid();
                    });
                }
                formValues[metaItem.elementName] = metaItem.elementValue;
            });

            const updatedActivityState = {
                ...initialHFCActivityState,
                id: uuid(),
                title: formValues.title,
                hostUsername: userStore.user?.displayName,
                typeid: formValues.assetType,
                type: selectedAsset,
                elementtypeid: formValues.elementType,
                element: selectedElement,
                number_of_element: formValues.print,
                order_tracking: "Received",
                asset_tracking_status: "New Asset Added",
                digitalization_priority: 0,
                calculated_category: 0,
                current_user: userStore.user?.displayName,
                current_task: "NA",
                current_subtask: "NA",
                physical_sensor_qrcode: item.container_barcode,
                physical_sensor_barcode: reel,
                physical_sensor_RFID: formValues.assetId,
                isActive: true,
                status: "NA",
                itemCreatedBy: userStore.user?.customerName,
                clearedon: new Date().toISOString(),
                maintenance: new Date().toISOString(),
                status_Time: new Date().toISOString(),
                movementdate: new Date().toISOString(),
                itemCreatedAt: new Date().toISOString(),
                movement_Time: new Date().toISOString(),
                receivingdate: new Date().toISOString(),
                projectEndDate: new Date().toISOString(),
                projectStartDate: new Date().toISOString(),
                synopsis: formValues.notes,
                metadata: clonedMetaData,
                assignedUsers: [
                    {
                        appUserId: userStore.user?.id,
                        username: userStore.user?.displayName,
                        displayName: userStore.user?.displayName,
                        bio: "",
                        image: userStore.user?.image,
                        roleId: userStore.user?.roleId
                    },
                ],
            };

            await agent.HFC.registerAssetHFC(itemGroupId, updatedActivityState);
        }
    }

    const payload = {
        ...historyInitialState,
        itemId: formValues.assetId,
        status: `${userStore.user?.displayName} has successfully registered the asset "${formValues.title}."`,
        dateTime: new Date().toISOString(),
        trackerFromTime: new Date().toISOString(),
    };

    await agent.HFC.HFCaddHistory(payload);

    setRegisterLoading(false);
    resetFormKeyValues();
    resetFormValues();
    dashboardStore.dashboard.valueForL3Inventory = '';
};

  
const debouncedTitleSearch = useMemo(
  () => debounce(async (value) => {
    if (value !== '') {
      try {
        const res = await agent.HFC.HFCRegisterTitle(value);
        setTitleSearch(res.data);
        setDropdownOpen(true);
      } catch (error) {
        console.error('Error fetching title search results:', error);
      }
    } else {
      setTitleSearch([]);
    }
  }, 500),
  [] // Empty dependency array ensures this is created once
);

// Handler for input changes
const handleInputChange = async (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>, index: number) => {
  const { name, value } = event.target;

  if (name === 'title') {
    debouncedTitleSearch(value);
  }

  if (index === -1) {
    setFormValues(prevFormValues => ({ ...prevFormValues, [name]: value }));
  } else {
    // Update the specific item in the containerBarcodes array
    setFormValues(prevFormValues => {
      const updatedContainerBarcodes = [...prevFormValues.containerBarcodes];
      updatedContainerBarcodes[index] = {
        ...updatedContainerBarcodes[index],
        [name]: value
      };
      return { ...prevFormValues, containerBarcodes: updatedContainerBarcodes };
    });
  }
};

  const handleTextAreaChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setFormValues({ ...formValues, [name]: value });
  };

  const updateMetaData = (updatedMetaData: any[]) => {
    setMetaData(updatedMetaData);
  };


  return (
    <div>
      <Tab
        style={{ marginTop: '20px' }}
        activeIndex={activeIndex}
        onTabChange={(_, data) => {
          if (typeof data.activeIndex === 'number') {
            setActiveIndex(data.activeIndex);
          }
        }}
        panes={[
          {
            menuItem: 'Asset',
            render: () => (
              <TabPane>
                <HFCAssetForm
                  formValues={formValues}
                  assetData={assetData}
                  elementData={elementData}
                  handleAssetTypeChange={handleAssetTypeChange}
                  handleElementTypeChange={handleElementTypeChange}
                  handleInputChange={handleInputChange}
                  goToNextTab={goToNextTab}
                  handleCheckboxChange={handleCheckboxChange} isChecked={isChecked}      
                  titleSearch= {titleSearch} 
                  setFormValues={setFormValues}     
                  dropdownOpen={dropdownOpen}  
                  setDropdownOpen={setDropdownOpen}    
                />
              </TabPane>
            )
          },
          {
            menuItem: 'Metadata',
            render: () => (
              <TabPane>
                 {
                  formValues.assetType && formValues.elementType  ? (
                      <HFCMetadata formValues={formValues} goToNextTab={goToNextTab} metaData={metaData} updateMetaData={updateMetaData}/>
                    ) : (
                     <strong>Kindly select the Asset type and Element type to see the Metadata details...!!!</strong>
                    )
                  }
              </TabPane>
            )
          },
          {
            menuItem: 'Physical',
            render: () => (
              <TabPane>
                <HFCPhysical setFormValues={setFormValues} formValues={formValues} handleInputChange={handleInputChange} handleTextAreaChange={handleTextAreaChange} />
              </TabPane>
            )
          }
        ]}
      />
      {activeIndex === 2 && (
        <div style={{textAlign: 'right', marginTop:'20px'}}>
         <Button primary onClick={handleSubmit} disabled={isSubmitDisabled() || registerLoading}>{registerLoading && <Loader active inline size="tiny" />}Submit</Button>
        </div>
      )}
    </div>
  );
}

export default observer(HfcRegister);
